import { useTranslation } from "react-i18next";
import {
  formatPrice,
  formatPriceWithCurrency,
  formatPriceWithLocalizedCurrency,
} from "@/lib/utils/priceUtils";

/**
 * React hook for price formatting with current locale awareness
 * @returns {Object} - Price formatting functions that use current i18n locale
 */
export const usePriceFormatter = () => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;

  return {
    /**
     * Formats a price number with locale-aware thousand separators
     * @param {number} price - The price to format
     * @returns {string} - Formatted price string
     */
    formatPrice: (price) => formatPrice(price, currentLocale),

    /**
     * Formats a price with currency using simple concatenation
     * @param {number} price - The price to format
     * @param {string} currency - The currency code
     * @returns {string} - Formatted price with currency
     */
    formatPriceWithCurrency: (price, currency) =>
      formatPriceWithCurrency(price, currency, currentLocale),

    /**
     * Formats a price with full localized currency formatting
     * @param {number} price - The price to format
     * @param {string} currency - The currency code
     * @returns {string} - Formatted price with localized currency
     */
    formatPriceWithLocalizedCurrency: (price, currency) =>
      formatPriceWithLocalizedCurrency(price, currency, currentLocale),
  };
};

export default usePriceFormatter;
